import {
  ExternalLink,
  Eye,
  EyeOff,
  Link as LinkIcon,
  RefreshCw,
  Settings,
  ShieldCheck,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { HealthState } from '../hooks/usePending.js'
import { short } from '../decode.js'
import { cn } from '@/lib/utils'
import { setPrivacy, usePrivacy } from '../lib/privacy.js'

interface Props {
  health: HealthState
  dappCount: number
  /** Resolved Safe pair when the visitor is in safeward mode; null on
   *  landing/onboarding so we don't advertise someone else's Safe. */
  safes: { mainSafe: string; moduleSafe: string } | null
  onRefresh: () => void
  onToggleSettings: () => void
  onOpenDApps: () => void
}

export function Header({
  health,
  dappCount,
  safes,
  onRefresh,
  onToggleSettings,
  onOpenDApps,
}: Props) {
  const privacy = usePrivacy()
  const { text, className } = badgeFor(health)
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <a
            href="?"
            className="flex items-center gap-2 hover:opacity-80"
            title="Back to landing"
          >
            <ShieldCheck className="size-6 text-primary" />
            SafeWard
          </a>
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Guardian Dashboard</p>
        {safes && (
          <div className="mt-3 flex flex-wrap gap-2">
            <SafeLink label="Main" address={safes.mainSafe} />
            <SafeLink label="Module" address={safes.moduleSafe} />
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={cn('px-2.5 py-1 text-[11px] uppercase tracking-wide', className)}
        >
          {text}
        </Badge>
        {safes && (
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenDApps}
              title="Pair / disconnect dApps"
            >
              <LinkIcon />
              dApps
              {dappCount > 0 && (
                <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {dappCount}
                </span>
              )}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPrivacy(!privacy)}
          aria-pressed={privacy}
          title={privacy ? 'Reveal addresses' : 'Hide addresses (screencast mode)'}
        >
          {privacy ? <EyeOff /> : <Eye />}
          {privacy ? 'Hidden' : 'Hide'}
        </Button>
        <appkit-button balance="hide" size="md" label="Connect signer" />
        <Button variant="ghost" size="sm" onClick={onToggleSettings}>
          <Settings />
          Settings
        </Button>
      </div>
    </header>
  )
}

function SafeLink({ label, address }: { label: string; address: string }) {
  const privacy = usePrivacy()
  const url = `https://app.safe.global/home?safe=eth:${address}`
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted"
      title={
        privacy
          ? `${label} Safe (hidden) — open in Safe{Wallet}`
          : `${label} Safe ${address} — open in Safe{Wallet}`
      }
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{short(address)}</span>
      <ExternalLink className="size-3 text-muted-foreground transition-colors group-hover:text-foreground" />
    </a>
  )
}

function badgeFor(h: HealthState): { text: string; className: string } {
  switch (h.kind) {
    case 'healthy':
      return {
        text: `healthy · ${h.pending} pending`,
        className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
      }
    case 'offline':
      return {
        text: 'offline',
        className: 'border-destructive/40 bg-destructive/15 text-destructive',
      }
    case 'auth-required':
      return {
        text: 'auth required',
        className: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
      }
    case 'unknown':
      return {
        text: 'starting up',
        className: 'border-border bg-muted text-muted-foreground',
      }
  }
}
